import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, History, User, Sun, Moon, LogOut, SquareUserIcon, Receipt, Gift } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

import { opsViewRoles } from '../lib/providerPricing';

export default function AppLayout() {
    const { theme, toggle } = useTheme();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const nav = [
        { to: "/dashboard", icon: Home, label: "Beranda" },
        { to: "/transaksi", icon: Receipt, label: "Transaksi" },
        { to: "/riwayat", icon: History, label: "Riwayat" },
        { to: "/bonus-warung", icon: Gift, label: "Bonus" },
        { to: "/profil", icon: User, label: "Profil" },
    ];

    if (["admin", "super_admin"].includes(user?.role)) {
        nav.push({ to: "/admin", icon: SquareUserIcon, label: "Admin" });
    }

    if (opsViewRoles.includes(user?.role)) nav.push({ to: "/admin/provider/digiflazz", icon: SquareUserIcon, label: "Provider" });

    const onLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col bg-background relative pb-24 shadow-sm shadow-black/5 dark:shadow-black/20 sm:shadow-xl">
                <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-xl border-b border-border">
                    <div className="mx-auto flex w-full items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
                        <div className="flex items-center gap-2">
                            <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
                                <img
                                    src="/images/LG_P4YU.png"
                                    alt="WarungPay Logo"
                                    className="w-full h-full object-contain"
                                />
                            </div>

                            <div>
                                <div className="font-heading font-extrabold text-lg leading-none tracking-tight">
                                    WARUNGPAY
                                </div>
                                <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                                    PPOB Indonesia
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <Button
                                data-testid="theme-toggle-btn"
                                variant="ghost"
                                size="icon"
                                className="rounded-full"
                                onClick={toggle}
                                aria-label="Toggle theme"
                            >
                                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                            </Button>
                            <Button
                                data-testid="logout-btn"
                                variant="ghost"
                                size="icon"
                                className="rounded-full"
                                onClick={onLogout}
                                aria-label="Logout"
                            >
                                <LogOut className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                </header>

                <main className="w-full px-4 py-5 sm:px-6 lg:px-8">
                    <Outlet />
                </main>

                {/* Bottom nav */}
                <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 z-40 w-full max-w-6xl bg-background/90 backdrop-blur-xl border-t border-border">
                    <div className="mx-auto grid max-w-6xl" style={{ gridTemplateColumns: `repeat(${nav.length}, minmax(0, 1fr))` }}>
                        {nav.map((item) => {
                            const Icon = item.icon;
                            const active = location.pathname.startsWith(item.to);
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`nav-${item.label.toLowerCase()}`}
                                    className="flex flex-col items-center justify-center py-3 gap-1 min-h-[64px] relative"
                                >
                                    <Icon className={`h-5 w-5 transition-colors ${active ? "text-brand-green" : "text-muted-foreground"}`} />
                                    <span className={`text-[11px] font-medium transition-colors ${active ? "text-brand-green" : "text-muted-foreground"}`}>
                                        {item.label}
                                    </span>
                                    {active && (
                                        <motion.div
                                            layoutId="nav-indicator"
                                            className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 bg-brand-green rounded-full"
                                        />
                                    )}
                                </NavLink>
                            );
                        })}
                    </div>
                </nav>
            </div>
        </div>
    );
}
